import { useEffect, useId, useState } from 'react';
import { usePagination } from 'react-instantsearch';

import type { RangeValues } from '@sorare/core/src/atoms/inputs/RangeSlider';
import { FilterSection } from '@sorare/core/src/components/search/FilterSection';
import { RangeSliderWithInputs } from '@sorare/core/src/components/search/RangeSliderWithInputs';
import {
  GqlFilterTitle,
  useBackendRangeSlider,
} from '@sorare/core/src/contexts/graphql-search';
import { useRangeSlider } from '@sorare/core/src/hooks/useRangeSlider';
import {
  fromAgeToMaxBirthDate,
  fromAgeToMinBirthDate,
  fromBirthDateToAge,
} from '@sorare/core/src/lib/birthDate';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import { FILTERS, type FilterWidget } from '@sorare/core/src/lib/filters';
import { clamp } from '@sorare/core/src/lib/math';

import { makeRangeFilter } from '#src/searchCards/RangeSlider/index.tsx';

type Props = {
  attribute: string;
  visible: boolean;
};

const RefineAge = ({ attribute, visible }: Props) => {
  const {
    refine,
    canRefine,
    range: { min, max },
    start: [currentRefinementMin, currentRefinementMax],
  } = useRangeSlider({
    attribute,
  });
  const track = useEvents();

  const localCurrentRefinementMax =
    currentRefinementMax === Infinity ? max || 0 : currentRefinementMax;
  const localCurrentRefinementMin =
    currentRefinementMin === -Infinity ? min || 0 : currentRefinementMin;

  // currentValues is expressed in years since the birth date
  const [currentValues, setCurrentValues] = useState({
    min: fromBirthDateToAge(localCurrentRefinementMax || max || 0), // on first load, when we don't have the min and max, we get null value, so the || 0 fixes the crash
    max: fromBirthDateToAge(localCurrentRefinementMin || min || 0),
  });
  const { refine: setPage } = usePagination();

  useEffect(() => {
    if (canRefine) {
      // oxlint-disable-next-line react/set-state-in-effect
      setCurrentValues(values => {
        if (
          fromBirthDateToAge(localCurrentRefinementMax || 0) !== values.max ||
          fromBirthDateToAge(localCurrentRefinementMin || 0) !== values.min
        ) {
          setCurrentValues({
            min: fromBirthDateToAge(localCurrentRefinementMax || 0),
            max: fromBirthDateToAge(localCurrentRefinementMin || 0),
          });
        }
        return values;
      });
    }
  }, [canRefine, localCurrentRefinementMax, localCurrentRefinementMin]);

  // If we don't have results, then don't show anything
  if (min === undefined || max === undefined) return null;

  const onValuesUpdated = (event: unknown, value: number | number[]) => {
    const values = value as number[];

    setCurrentValues({
      min: values[0],
      max: values[1],
    });
  };

  const onChange = (event: unknown, value: number | number[]) => {
    const values = value as number[];
    const nextMin = Math.max(min, fromAgeToMinBirthDate(values[1]));
    const nextMax = Math.min(max, fromAgeToMaxBirthDate(values[0]));

    if (currentRefinementMin !== nextMin || currentRefinementMax !== nextMax) {
      setPage(0);
      refine([nextMin, nextMax]);
      track('Use Market Filter', {
        filterName: attribute,
        filterValue: `${Math.floor(nextMin)}-${Math.ceil(nextMax)}`,
      });
    }
  };

  const computedMin = fromBirthDateToAge(max);
  const computedMax = fromBirthDateToAge(min);

  const minValue = clamp(currentValues.min, computedMin, computedMax);
  const maxValue = clamp(currentValues.max, computedMin, computedMax);

  if (!visible) return <FilterSection />;

  return (
    <FilterSection>
      <RangeSliderWithInputs
        id="refine-age"
        min={computedMin}
        max={computedMax}
        rangeValues={{
          low: minValue,
          high: maxValue,
        }}
        onChange={(range: RangeValues) => {
          onValuesUpdated(null, [range.low, range.high]);
        }}
        onChangeCommitted={(range: RangeValues) =>
          onChange(null, [range.low, range.high])
        }
      />
    </FilterSection>
  );
};

export const RefineCardPlayerAge = makeRangeFilter(
  ({ visible }) => (
    <RefineAge attribute={FILTERS.age.attribute} visible={visible} />
  ),
  FILTERS.age
);

const dateToAge = (date: number | undefined) =>
  date != null ? fromBirthDateToAge(date) : undefined;

const RefineBackendAge = ({ visible }: { visible: boolean }) => {
  const id = useId();

  const {
    canRefine,
    refine,
    range: { min, max },
    start: [currentRefinementMin, currentRefinementMax],
  } = useBackendRangeSlider({ attribute: 'age' });

  const localCurrentRefinementMax =
    currentRefinementMax === undefined ? max : currentRefinementMax;
  const localCurrentRefinementMin =
    currentRefinementMin === undefined ? min : currentRefinementMin;

  const currentRefinementAgeMin = dateToAge(localCurrentRefinementMax);
  const currentRefinementAgeMax = dateToAge(localCurrentRefinementMin);

  // currentValues is expressed in years since the birth date
  const [currentValues, setCurrentValues] = useState({
    min: currentRefinementAgeMin,
    max: currentRefinementAgeMax,
  });

  useEffect(() => {
    if (canRefine && max != null && min != null) {
      const computedMin = fromBirthDateToAge(max);
      const computedMax = fromBirthDateToAge(min);
      // oxlint-disable-next-line react/set-state-in-effect
      setCurrentValues(prev => {
        const { min: prevMin = -Infinity, max: prevMax = Infinity } = prev;
        if (prevMin < computedMin || prevMax > computedMax) {
          return {
            min: prevMin <= computedMin ? undefined : prev.min,
            max: prevMax >= computedMax ? undefined : prevMax,
          };
        }
        return prev;
      });
    }
  }, [min, max, canRefine]);

  // If we don't have results, then don't show anything
  if (min === undefined || max === undefined) return null;

  const computedMin = fromBirthDateToAge(max);
  const computedMax = fromBirthDateToAge(min);

  const minValue = clamp(
    currentValues.min ?? -Infinity,
    computedMin,
    computedMax
  );
  const maxValue = clamp(
    currentValues.max ?? Infinity,
    computedMin,
    computedMax
  );

  if (!visible) {
    return <FilterSection />;
  }

  return (
    <FilterSection>
      <RangeSliderWithInputs
        id={id}
        min={computedMin}
        max={computedMax}
        rangeValues={{ low: minValue, high: maxValue }}
        onChange={rangeValues => {
          setCurrentValues({
            min: rangeValues.low <= computedMin ? undefined : rangeValues.low,
            max: rangeValues.high >= computedMax ? undefined : rangeValues.high,
          });
        }}
        onChangeCommitted={rangeValues => {
          refine([
            rangeValues.high === computedMax
              ? undefined
              : fromAgeToMinBirthDate(rangeValues.high),
            rangeValues.low === computedMin
              ? undefined
              : fromAgeToMaxBirthDate(rangeValues.low),
          ]);
        }}
      />
    </FilterSection>
  );
};

export const RefineBackendCardPlayerAge = {
  key: 'age',
  type: 'list',
  attribute: 'age',
  Component: ({ visible }) => <RefineBackendAge visible={visible} />,
  title: <GqlFilterTitle name="age" title={FILTERS.age.title} />,
  accordionOptions: { startsOpen: false },
} satisfies FilterWidget;
